/*=========================================================================================
  File Name: unAuthActions.js
  Description: Vuex Store - auth related actions
==========================================================================================*/

export default {
    /**
     * get unAuth screen
     *
     * @param commit
     * @param payload
     * @returns {Promise<unknown>}
     */
    fetchFirstUnAuthScreen({commit}, payload) {
        if (!state.currentTranslations) {
            commit('fetch_first_un_auth_screen');
            return new Promise((resolve, reject) => {
                let locale = '';
                if (payload.locale !== null) {
                    locale = '/' + payload.locale
                }

                axios.all([
                    axios({url: state.API_URL + '/auth/settings', method: 'GET'}),
                    axios({url: state.API_URL + '/auth/current-translations' + locale, method: 'GET'}),
                    axios({url: state.API_URL + '/auth/active-languages', method: 'GET'}),
                    axios({url: state.API_URL + '/auth/language' + locale, method: 'GET'}),
                    axios({url: state.API_URL + '/auth/countries', method: 'GET'})
                ]).then(axios.spread((settings, translations, languages, language, countries) => {
                    let data = {
                        basicOrganizationData: settings.data.basicOrganizationData,
                        themeSettings: settings.data.themeSettings,
                        currentTranslations: translations.data.currentTranslations,
                        activeLanguage: language.data,
                        menus: {
                            languageMenu: languages.data,
                        },
                        countries: countries.data,
                    };

                    localStorage.setItem('primary_color', data.themeSettings !== null ? data.themeSettings.primary_color : state.defaultTheme.primary_color);

                    commit('first_un_auth_screen_fetched', data);

                    resolve();
                })).catch(() => {
                    commit('fetch_first_un_auth_screen_error');
                    reject();
                });
            })
        }
    },

    /**
     * change active user languag   e
     *
     * @param commit
     * @param payload
     * @returns {Promise<unknown>}
     */
    change_active_language({commit}, payload) {
        return new Promise((resolve) => {
            commit('change_active_language');
            axios({url: state.API_URL + '/auth/change-active-language', data: payload, method: 'POST'})
                .then(({data}) => {
                    axios.all([
                        axios({url: state.API_URL + '/auth/language/' + payload.locale, method: 'GET'}),
                        axios({url: state.API_URL + '/auth/current-translations/' + payload.locale, method: 'GET'}),
                    ]).then(axios.spread((language, translations, menu) => {

                        let lang_data = {
                            activeLanguage: language.data,
                            currentTranslations: translations.data.currentTranslations,
                        };

                        commit('active_language_changed', lang_data);

                        payload.notify({
                            time: 3500,
                            text: data.message,
                            iconPack: 'feather',
                            icon: 'icon-check',
                            color: 'success'
                        });

                        resolve(language);
                    }));
                })
        })
    },

    /**
     * authorization
     *
     * @param commit
     * @param payload
     * @returns {Promise<unknown>}
     */
    login({commit}, payload) {
        return new Promise((resolve, reject) => {
            commit('auth_request');
            axios({url: state.API_URL + '/auth/login', data: payload.userData, method: 'POST'})
                .then(({data}) => {
                    if (data.access_token) {
                        const auth_data = {
                            token: data.token_type + ' ' + data.access_token,
                            user_id: data.user_id,
                            message: data.message
                        };

                        localStorage.setItem('access-token', auth_data.token);
                        axios.defaults.headers.common['Accept'] = 'application/json';
                        axios.defaults.headers.common['Authorization'] = auth_data.token;
                        localStorage.setItem('user_id', auth_data.user_id);
                        commit('auth_success', auth_data);

                        resolve();
                    } else {
                        commit('auth_2fa', data.message);
                        if (payload.closeAnimation) payload.closeAnimation();

                        resolve(data);
                    }
                }).catch(({response}) => {
                commit('auth_error', response.data);
                localStorage.removeItem('access-token');
                reject(response.data);
            });
        })
    },

    /**
     * authorization confirm
     *
     * @param commit
     * @param payload
     * @returns {Promise<unknown>}
     */
    loginConfirm({commit}, payload) {
        return new Promise((resolve, reject) => {
            commit('auth_request');
            axios({url: state.API_URL + '/auth/login/confirm', data: payload.userData, method: 'POST'})
                .then(({data}) => {
                    const auth_data = {
                        token: data.token_type + ' ' + data.access_token,
                        user_id: data.user_id,
                        message: data.message
                    };

                    localStorage.setItem('access-token', auth_data.token);
                    axios.defaults.headers.common['Authorization'] = auth_data.token;
                    localStorage.setItem('user_id', auth_data.user_id);
                    commit('auth_success', auth_data);

                    if (payload.closeAnimation) payload.closeAnimation();

                    resolve();
                }).catch(({response}) => {
                commit('auth_error', response.data);
                localStorage.removeItem('access-token');
                reject(response.data);
            });
        })
    },

    /**
     * resend any login confirm
     *
     * @param commit
     * @param payload
     * @returns {Promise<unknown>}
     */
    resendLoginConfirm({commit}, payload) {
        return new Promise((resolve, reject) => {
            axios({url: state.API_URL + '/auth/login/resend-confirm', data: payload, method: 'POST'})
                .then((response) => {
                    if (payload.closeAnimation) payload.closeAnimation();
                    commit('resend_auth_2fa_success', response.data);
                    resolve(response.data);
                }).catch((error) => {
                commit('resend_auth_2fa_error', error.response.data);
                reject();
            })
        });
    },

    /**
     * get forgot password screen
     *
     * @param commit
     * @param payload
     * @returns {Promise<unknown>}
     */
    recoverPassword({commit}, payload) {
        return new Promise((resolve, reject) => {
            axios({url: state.API_URL + '/auth/recover-password', data: payload, method: 'POST'})
                .then((response) => {
                    commit('password_recover_success', response.data);
                    resolve();
                }).catch((error) => {
                commit('password_recover_error', error.response.data);
                reject(error.response.data);
            })
        });
    },

    /**
     * get sign up screen
     *
     * @param commit
     * @param payload
     * @returns {Promise<unknown>}
     */
    signUp({commit}, payload) {
        return new Promise((resolve, reject) => {
            axios({url: state.API_URL + '/auth/register', data: payload, method: 'POST'})
                .then((response) => {
                    commit('sign_up_success', response.data);
                    resolve();
                }).catch((error) => {
                commit('sign_up_error', error.response.data);
                reject(error.response.data);
            })
        });
    },

    /**
     * confirm any email related confirm
     *
     * @param commit
     * @param payload
     * @returns {Promise<unknown>}
     */
    confirmAction({commit}, payload) {
        return new Promise((resolve, reject) => {
            axios({url: state.API_URL + '/auth/confirm', data: payload, method: 'POST'})
                .then(({data}) => {
                    if (payload.closeAnimation) payload.closeAnimation();
                    commit('confirm_success', data);
                    resolve(data);
                }).catch(({response}) => {
                commit('confirm_error', response.data);
                reject(response.data);
            })
        });
    },

    /**
     * resend any email related confirm
     *
     * @param commit
     * @param payload
     * @returns {Promise<unknown>}
     */
    resendConfirm({commit}, payload) {
        return new Promise((resolve, reject) => {
            axios({url: state.API_URL + '/auth/resend-confirm', data: payload, method: 'POST'})
                .then((response) => {
                    if (payload.closeAnimation) payload.closeAnimation();
                    commit('resend_confirm_success', response.data);
                    resolve();
                }).catch((error) => {
                commit('resend_confirm_error', error.response.data);
                reject();
            })
        });
    },

    /**
     * get reset password screen
     *
     * @param commit
     * @param payload
     * @returns {Promise<unknown>}
     */
    recoverPasswordConfirm({commit}, payload) {
        return new Promise((resolve, reject) => {
            axios({url: state.API_URL + '/auth/recover-password-confirm', data: payload, method: 'POST'})
                .then(({data}) => {
                    const auth_data = {
                        token: data.token_type + ' ' + data.access_token,
                        message: data.message
                    };

                    localStorage.setItem('access-token', auth_data.token);
                    axios.defaults.headers.common['Authorization'] = auth_data.token;

                    if (payload.closeAnimation) payload.closeAnimation();

                    commit('password_recover_confirm_success', auth_data);
                    resolve();
                }).catch(({response}) => {
                commit('password_recover_confirm_error', response.data);
                reject(response.data);
            })
        });
    },

    /**
     * change password confirm
     *
     * @param commit
     * @param payload
     * @returns {Promise<unknown>}
     */
    changePasswordConfirm({commit}, payload) {
        return new Promise((resolve, reject) => {
            axios({url: state.API_URL + '/auth/change-password-confirm', data: payload, method: 'POST'})
                .then(({data}) => {
                    const auth_data = {
                        token: data.token_type + ' ' + data.access_token,
                        message: data.message
                    };

                    localStorage.setItem('access-token', auth_data.token);
                    axios.defaults.headers.common['Authorization'] = auth_data.token;

                    if (payload.closeAnimation) payload.closeAnimation();

                    commit('password_change_confirm_success', auth_data);
                    resolve();
                }).catch(({response}) => {
                commit('password_change_confirm_error', response.data);
                reject(response.data);
            })
        });
    },

    /**
     * change email confirm
     *
     * @param commit
     * @param payload
     * @returns {Promise<unknown>}
     */
    changeEmailConfirm({commit}, payload) {
        return new Promise((resolve, reject) => {
            axios({url: state.API_URL + '/auth/change-email-confirm', data: payload, method: 'POST'})
                .then(({data}) => {
                    const auth_data = {
                        token: data.token_type + ' ' + data.access_token,
                        message: data.message
                    };

                    localStorage.setItem('access-token', auth_data.token);
                    axios.defaults.headers.common['Authorization'] = auth_data.token;

                    if (payload.closeAnimation) payload.closeAnimation();

                    commit('email_change_confirm_success', auth_data);
                    resolve();
                }).catch(({response}) => {
                commit('email_change_confirm_error', response.data);
                reject(response.data);
            })
        });
    },
};
import axios from "axios";
import state from "../state";
